<template>
  <!--
    CASL abilities required (issued by backend on login in userData.abilities):
      { action: 'read', subject: 'org_chart' }   — open this page / see profile menu item
      { action: 'manage', subject: 'org_chart' } — Show inactive
    Also accepted: { action: 'manage', subject: 'all' }
    Node click expands that person's direct reports (accordion: one branch at a time).
    Details open from the info control (or from a leaf card). Default is collapsed (roots only).
  -->
  <div class="org-chart-page">
    <!-- Navbar already shows pageTitle — subtitle + badges only -->
    <div class="org-chart-page__header d-flex align-items-center justify-content-between mb-1 flex-wrap">
      <small class="text-muted mb-0">
        Reporting hierarchy · Click a person to show their team
        <span v-if="rootsLabel"> · {{ rootsLabel }}</span>
      </small>
      <div class="d-flex align-items-center flex-wrap">
        <b-badge
          v-if="!loading && peopleCount"
          pill
          variant="light-primary"
          class="org-chart-page__count"
        >
          {{ peopleCount }} {{ peopleCount === 1 ? 'person' : 'people' }}
        </b-badge>
        <b-badge
          v-if="fromCache && !loading && peopleCount"
          pill
          variant="light-secondary"
          class="ml-50 org-chart-page__count"
        >
          Cached
        </b-badge>
      </div>
    </div>

    <org-chart-toolbar
      :search.sync="searchQuery"
      :scale="scale"
      :show-inactive.sync="showInactive"
      :my-team-only.sync="myTeamOnly"
      :can-show-inactive="canManage"
      :can-my-team="canMyTeamToggle"
      @expand-all="expandAll"
      @collapse-all="collapseAll"
      @zoom-in="zoomBy(0.1)"
      @zoom-out="zoomBy(-0.1)"
      @reset-view="resetView"
      @fit-view="fitView"
    />

    <b-alert v-if="error" show variant="danger" class="mb-1" dismissible @dismissed="error = null">
      {{ error }}
    </b-alert>

    <div v-if="loading" class="org-chart-page__state text-center">
      <b-spinner variant="primary" label="Loading org chart" />
      <p class="text-muted mt-1 mb-0">Loading employees…</p>
    </div>

    <div v-else-if="!forest.roots.length" class="org-chart-page__state org-chart-page__empty text-center">
      <feather-icon icon="UsersIcon" size="36" class="text-muted mb-1" />
      <h5 class="mb-50">No employees to display</h5>
      <p class="text-muted mb-0 small">
        Check reporting manager data (<code>mgr_id</code>) or try enabling inactive employees if you have access.
      </p>
    </div>

    <org-chart-canvas
      v-else
      ref="canvas"
      :roots="forest.roots"
      :collapsed-set="collapsedSet"
      :highlight-set="highlightSet"
      :selected-norm="selectedNorm"
      :scale="scale"
      :translate="translate"
      @toggle="toggleNode"
      @select="onSelectNode"
      @zoom-delta="zoomBy"
      @pan-delta="onPanDelta"
      @reset-view="resetView"
    />

    <b-modal
      id="org-chart-detail-modal"
      v-model="detailOpen"
      title="Employee details"
      ok-only
      ok-title="Close"
      ok-variant="primary"
      centered
      size="md"
      body-class="org-chart-detail"
    >
      <div v-if="detailUser" class="org-chart-detail__wrap">
        <div class="d-flex align-items-start mb-1">
          <b-avatar
            size="56"
            :src="detailAvatar || undefined"
            :text="detailInitials"
            variant="light-primary"
            class="mr-1 flex-shrink-0"
          />
          <div class="min-width-0">
            <h4 class="mb-0 text-truncate">{{ detailName }}</h4>
            <b-badge
              v-if="!detailActive"
              pill
              variant="light-secondary"
              class="mt-50"
            >
              Inactive
            </b-badge>
          </div>
        </div>

        <b-row class="org-chart-detail__fields">
          <b-col v-if="detailDept" cols="12" sm="6" class="mb-75">
            <div class="org-chart-detail__label">Department</div>
            <div class="org-chart-detail__value">{{ detailDept }}</div>
          </b-col>
          <b-col cols="12" sm="6" class="mb-75">
            <div class="org-chart-detail__label">Designation</div>
            <div class="org-chart-detail__value">{{ detailDesignation || '—' }}</div>
          </b-col>
          <b-col cols="12" sm="6" class="mb-75">
            <div class="org-chart-detail__label">Vertical</div>
            <div class="org-chart-detail__value">{{ detailVertical || '—' }}</div>
          </b-col>
          <b-col v-if="detailEmail" cols="12" sm="6" class="mb-75">
            <div class="org-chart-detail__label">Email</div>
            <div class="org-chart-detail__value">
              <a :href="`mailto:${detailEmail}`">{{ detailEmail }}</a>
            </div>
          </b-col>
          <b-col v-if="detailManagerName" cols="12" sm="6" class="mb-75">
            <div class="org-chart-detail__label">Manager</div>
            <div class="org-chart-detail__value">{{ detailManagerName }}</div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BSpinner,
  BModal,
  BAvatar,
  BBadge,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import { fetchOrgChartUsers } from '@/services/org-chart-service'
import {
  canManageOrgChart,
} from '@/utils/org-chart-permissions'
import {
  filterUsersByActive,
  buildOrgForest,
  getSubtreeForest,
  collectAncestorNormIds,
  searchOrgNodes,
  defaultCollapsedSet,
  DEFAULT_ORG_EXPAND_DEPTH,
  exclusiveExpandCollapsedSet,
  allCollapsibleNormIds,
  displayName,
  displayDesignation,
  displayVertical,
  displayAvatar,
  displayEmail,
  isActiveUser,
} from '@/utils/org-chart-tree'
import OrgChartToolbar from './components/OrgChartToolbar.vue'
import OrgChartCanvas from './components/OrgChartCanvas.vue'

const MIN_SCALE = 0.4
const MAX_SCALE = 1.8

export default {
  name: 'OrgChart',
  components: {
    BAlert,
    BSpinner,
    BModal,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    OrgChartToolbar,
    OrgChartCanvas,
  },
  data() {
    return {
      loading: true,
      error: null,
      rawUsers: [],
      fromCache: false,
      searchQuery: '',
      showInactive: false,
      myTeamOnly: false,
      scale: 1,
      translate: { x: 0, y: 0 },
      collapsedSet: new Set(),
      highlightSet: new Set(),
      selectedNorm: null,
      detailOpen: false,
      detailUser: null,
      detailManagerName: '',
      searchTimer: null,
      currentEmpId: null,
    }
  },
  computed: {
    canManage() {
      return canManageOrgChart((action, subject) => this.$can(action, subject))
    },
    canMyTeamToggle() {
      return !!this.currentEmpId
    },
    filteredUsers() {
      return filterUsersByActive(this.rawUsers, this.showInactive && this.canManage)
    },
    fullForest() {
      return buildOrgForest(this.filteredUsers)
    },
    forest() {
      if (this.myTeamOnly && this.currentEmpId) {
        return getSubtreeForest(this.fullForest.nodeByEmpId, this.currentEmpId)
      }
      return this.fullForest
    },
    peopleCount() {
      return this.forest.nodeByEmpId ? this.forest.nodeByEmpId.size : 0
    },
    rootsLabel() {
      const roots = this.forest.roots ? this.forest.roots.length : 0
      if (!roots || !this.peopleCount) return ''
      return `${roots} root${roots === 1 ? '' : 's'}`
    },
    detailName() {
      return displayName(this.detailUser)
    },
    detailDesignation() {
      return displayDesignation(this.detailUser)
    },
    detailDept() {
      if (!this.detailUser) return ''
      return String(this.detailUser.department || '').trim()
    },
    detailVertical() {
      return displayVertical(this.detailUser)
    },
    detailAvatar() {
      return displayAvatar(this.detailUser)
    },
    detailEmail() {
      return displayEmail(this.detailUser)
    },
    detailActive() {
      return isActiveUser(this.detailUser)
    },
    detailInitials() {
      return avatarText(this.detailName)
    },
  },
  watch: {
    filteredUsers() {
      this.resetCollapsedForForest()
    },
    myTeamOnly() {
      this.resetCollapsedForForest()
    },
    searchQuery(val) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.applySearch(val), 220)
    },
  },
  created() {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      this.currentEmpId = userData.emp_id || null
    } catch (e) {
      this.currentEmpId = null
    }
    this.loadUsers()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    async loadUsers() {
      this.loading = true
      this.error = null
      try {
        const cached = (() => {
          try {
            return JSON.parse(localStorage.getItem('allusers') || '[]')
          } catch (e) {
            return []
          }
        })()
        if (Array.isArray(cached) && cached.length) {
          this.rawUsers = cached
          this.fromCache = true
          this.loading = false
          this.resetCollapsedForForest()
        }

        const { users, fromCache } = await fetchOrgChartUsers()
        this.rawUsers = users
        this.fromCache = fromCache
        if (!users.length) {
          this.error = 'Could not load employees for the organization chart.'
        }
        this.resetCollapsedForForest()
      } catch (err) {
        console.error('[org-chart] load failed', err)
        this.error = 'Failed to load organization chart data.'
      } finally {
        this.loading = false
      }
    },
    resetCollapsedForForest() {
      this.collapsedSet = defaultCollapsedSet(this.forest.roots, DEFAULT_ORG_EXPAND_DEPTH)
      this.highlightSet = new Set()
      this.selectedNorm = null
    },
    toggleNode(empIdNorm) {
      const opening = this.collapsedSet.has(empIdNorm)
      if (!opening) {
        const next = new Set(this.collapsedSet)
        next.add(empIdNorm)
        this.collapsedSet = next
        this.focusOpenedNode(empIdNorm, { adjustScale: false, includeChildren: false })
        return
      }
      this.collapsedSet = exclusiveExpandCollapsedSet(
        this.forest.roots,
        this.forest.nodeByEmpId,
        empIdNorm,
      )
      this.focusOpenedNode(empIdNorm, { adjustScale: true, includeChildren: true })
    },
    focusOpenedNode(empIdNorm, options = {}) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const canvasCmp = this.$refs.canvas
            if (!canvasCmp || !canvasCmp.computeFocusTransform) return
            const focused = canvasCmp.computeFocusTransform(empIdNorm, options)
            if (!focused) return
            if (typeof canvasCmp.beginViewAnimation === 'function') {
              canvasCmp.beginViewAnimation()
            }
            this.scale = focused.scale
            this.translate = focused.translate
          })
        })
      })
    },
    expandAll() {
      this.collapsedSet = new Set()
    },
    collapseAll() {
      this.collapsedSet = new Set(allCollapsibleNormIds(this.forest.roots))
    },
    zoomBy(delta) {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale + delta))
      this.scale = Math.round(next * 100) / 100
    },
    resetView() {
      this.scale = 1
      this.translate = { x: 0, y: 0 }
    },
    fitView() {
      this.$nextTick(() => {
        const canvasCmp = this.$refs.canvas
        if (!canvasCmp || !canvasCmp.computeFitTransform) return
        const fit = canvasCmp.computeFitTransform()
        if (!fit) return
        if (typeof canvasCmp.beginViewAnimation === 'function') {
          canvasCmp.beginViewAnimation()
        }
        this.scale = fit.scale
        this.translate = fit.translate
      })
    },
    onPanDelta({ dx, dy }) {
      this.translate = {
        x: this.translate.x + dx,
        y: this.translate.y + dy,
      }
    },
    applySearch(query) {
      const q = String(query || '').trim()
      if (!q) {
        this.highlightSet = new Set()
        return
      }
      const matches = searchOrgNodes(this.forest.nodeByEmpId, q)
      this.highlightSet = new Set(matches)
      if (!matches.length) return

      // Expand ancestors of all matches so cards are visible
      const nextCollapsed = new Set(this.collapsedSet)
      matches.forEach(empNorm => {
        const ancestors = collectAncestorNormIds(this.forest.nodeByEmpId, empNorm)
        ancestors.forEach(id => nextCollapsed.delete(id))
      })
      this.collapsedSet = nextCollapsed

      this.focusOpenedNode(matches[0], { adjustScale: false })
    },
    onSelectNode(node) {
      this.openDetail(node)
    },
    openDetail(node) {
      this.detailUser = node.user
      this.selectedNorm = node.empIdNorm
      let managerName = ''
      if (node.managerEmpIdNorm && this.fullForest.nodeByEmpId.has(node.managerEmpIdNorm)) {
        managerName = displayName(this.fullForest.nodeByEmpId.get(node.managerEmpIdNorm).user)
      } else if (node.managerEmpId) {
        managerName = String(node.managerEmpId)
      }
      this.detailManagerName = managerName
      this.detailOpen = true
    },
  },
}
</script>

<style lang="scss" scoped>
// Navbar already shows pageTitle — reclaim the large content-wrapper gap
// (same pattern as Performance Dashboard / Reimbursement). Scoped to this page only.
.org-chart-page {
  min-height: 60vh;
  margin-top: -0.85rem;

  ::v-deep .card,
  ::v-deep .btn,
  ::v-deep .btn:hover,
  ::v-deep .btn:focus,
  ::v-deep .btn:active {
    box-shadow: none !important;
  }

  &__header {
    gap: 0.5rem;
  }

  &__count {
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  &__state {
    padding: 3.5rem 1.25rem;
    background: #f8f8f8;
    border: 1px dashed #d8d6de;
    border-radius: 0.5rem;
  }

  &__empty {
    h5 {
      color: #5e5873;
    }
  }
}

.org-chart-detail {
  &__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6e6b7b;
    margin-bottom: 0.15rem;
  }

  &__value {
    font-size: 0.9rem;
    color: #5e5873;
    word-break: break-word;

    a {
      color: #00ac99;
    }
  }
}
</style>
